<template>
  <div v-if="resolvedTags.length" class="tag-chips" :class="`tag-chips--${size}`">
    <div
      v-for="(tag, i) in resolvedTags"
      :key="tag.key"
      class="tag-pill"
      :class="{ 'tag-pill--auto': tag.auto }"
      :style="{
        '--tag-color': TAG_COLORS[tag.color] || TAG_COLORS.grey,
        'animation-delay': `${i * 50}ms`,
      }">
      <span class="tag-pill__dot" />
      <span class="tag-pill__label">{{ tag.label }}</span>
      <button
        v-if="removable && !tag.auto"
        type="button"
        class="tag-pill__remove"
        :aria-label="`Remove tag ${tag.label}`"
        @click.stop="onRemove(tag.key)">
        <v-icon :size="size === 'md' ? 13 : 11">mdi-close</v-icon>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { filterUserTags } from "@/utils/tags";

const props = defineProps({
  tags: { type: Array, default: () => [] },
  tagDefs: { type: Array, default: () => [] },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
  removable: { type: Boolean, default: false },
});

const emit = defineEmits(["update:tags"]);

const defMap = computed(() => {
  const map = {};
  for (const def of props.tagDefs) {
    map[def.key] = def;
  }
  return map;
});

const resolvedTags = computed(() => (props.tags || []).map((key) => defMap.value[key]).filter(Boolean));

function onRemove(key) {
  emit(
    "update:tags",
    filterUserTags(props.tags, props.tagDefs).filter((t) => t !== key)
  );
}
</script>

<style lang="scss" scoped>
.tag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  padding: 2px 6px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--tag-color) 8%, transparent);
  animation: tag-fade-in 0.3s ease-out both;
}

.tag-pill--auto {
  border: 1px dotted color-mix(in srgb, var(--tag-color) 40%, transparent);
}

.tag-pill__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--tag-color);
  flex-shrink: 0;
}

.tag-pill__label {
  font-family: "DM Sans", sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--tag-color);
  line-height: 1;
  white-space: nowrap;
}

.tag-pill__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin: 0 -2px 0 -1px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--tag-color);
  opacity: 0.45;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    opacity: 1;
    background: color-mix(in srgb, var(--tag-color) 15%, transparent);
  }
}

@media (pointer: coarse) {
  .tag-pill__remove {
    opacity: 0.65;
  }
}

// Larger variant for detail pages
.tag-chips--md {
  gap: 6px;

  .tag-pill {
    height: 26px;
    padding: 0 12px;
    border-radius: 13px;
    gap: 6px;
    background: color-mix(in srgb, var(--tag-color) 10%, transparent);
  }

  .tag-pill__dot {
    width: 6px;
    height: 6px;
  }

  .tag-pill__label {
    font-size: 11.5px;
    letter-spacing: 0.04em;
  }

  .tag-pill__remove {
    width: 18px;
    height: 18px;
    margin: 0 -6px 0 -2px;
  }
}

@keyframes tag-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
